// START IMPORTS
import { useSubscriptionAccessConfigContext } from '@ltvco/refresh-lib/ctx';
import {
  type ActiveTab,
  ExperimentHeader,
  StandardSearchPage,
} from '@ltvco/refresh-lib/experiments/RFRSH-4335';
import {
  MoreSearchesDropdown,
  SearchPreferencesModal,
  useSearchPreferences,
  type SearchPreferenceId,
} from '@ltvco/refresh-lib/experiments/RFRSH-4625';
import { Box, Dialog } from '@ltvco/refresh-lib/theme';
import { useSession } from '@ltvco/refresh-lib/v1';
import {
  Header,
  HeaderMenu,
  Lang,
  MenuToggle,
  NotificationBell,
  NotificationDropdown,
} from '@ltvco/refresh-lib/v2';
import { useFeatureIsOn } from '@ltvco/refresh-lib/vendors';
import React, { useEffect, useMemo, useState } from 'react';

import { MobileDownloadSnackbar } from 'components/MobileDownloadSnackbar/MobileDownloadSnackbar';

// END IMPORTS

// START INTERFACES_AND_TYPES
type HeaderParentProps = {
  handleSearchSuccess: any;
  changeSingleReportVariation: (variation: boolean) => void;
  changeOwnerSearchVariation: (variation: boolean) => void;
  changeIsInAstrologyStandaloneVariation: (variation: boolean) => void;
  changeInternationalLanguageTest: (variation: boolean) => void;
  changeInternationalVinTest: (variation: boolean) => void;
  changeListV2Variation: (variation: boolean) => void;
  changeChatBVFlowFooterVariation: (variation: boolean) => void;
};
// END INTERFACES_AND_TYPES

export const HeaderParent = ({
  handleSearchSuccess,
  changeSingleReportVariation,
  changeIsInAstrologyStandaloneVariation,
  changeInternationalLanguageTest,
  changeInternationalVinTest,
  changeListV2Variation,
  changeChatBVFlowFooterVariation,
}: HeaderParentProps) => {
  const {
    session: { account },
  } = useSession();

  // RFRSH-4335: Flag check lives here in BV, not in the lib
  const isInStandardSearchDesign = useFeatureIsOn('RFRSH-4335');
  const [isSearchPageOpen, setIsSearchPageOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [notificationAnchorEl, setNotificationAnchorEl] =
    useState<null | HTMLElement>(null);

  // 4625 search preferences: same store the dashboard uses so a toggle from
  // the header search page immediately reflects on the dashboard (and vice
  // versa) — no separate preferences per surface.
  const preferences = useSearchPreferences();
  const [isPrefsModalOpen, setIsPrefsModalOpen] = useState(false);
  const openPrefsModal = () => setIsPrefsModalOpen(true);

  // Filter+order tabs from the user's saved preferences. Mirrors SearchHub's
  // composition so the header search page renders the same set of tabs (in
  // the same order) as the dashboard search.
  // SearchPreferenceId and ActiveTab share the same form-backed id union, so
  // selected preference ids map 1:1 onto StandardSearchPage tabs.
  const searchPageVisibleTabs = useMemo(() => {
    if (!isInStandardSearchDesign) return undefined;
    return preferences.selectedIds.map((id) => ({ id: id as ActiveTab }));
  }, [isInStandardSearchDesign, preferences.selectedIds]);

  const isInAstrologyStandaloneVariation = useFeatureIsOn('oar-1164');
  const isInListV2Variation = useFeatureIsOn('prop-2834');
  const isOnChatBVFlowFooterVariation = useFeatureIsOn('prop-3640');
  const UKFraudScanVariation = useFeatureIsOn('RTRN-1565');

  const {
    singleReportVariation,
    internationalLanguageVariation,
    vinVariation,
  } = useSubscriptionAccessConfigContext();
  // END HOOKS_AND_QUERIES

  // START EFFECTS
  useEffect(() => {
    changeSingleReportVariation(singleReportVariation);
  }, [singleReportVariation]);

  useEffect(() => {
    changeIsInAstrologyStandaloneVariation(isInAstrologyStandaloneVariation);
  }, [isInAstrologyStandaloneVariation]);

  useEffect(() => {
    changeInternationalLanguageTest(internationalLanguageVariation);
  }, [internationalLanguageVariation]);

  useEffect(() => {
    changeInternationalVinTest(vinVariation);
  }, [vinVariation]);

  useEffect(() => {
    changeListV2Variation(isInListV2Variation);
  }, [isInListV2Variation]);

  useEffect(() => {
    changeChatBVFlowFooterVariation(isOnChatBVFlowFooterVariation);
  }, [isOnChatBVFlowFooterVariation]);

  if (location.pathname.includes('/chatbv')) {
    return null;
  }
  // END EARLY_RETURNS

  // START RENDER
  const spanishPlanEnabled =
    account?.account?.language_info?.plan_language === 'es';
  const portuguesePlanEnabled =
    account?.account?.language_info?.plan_language === 'pt';
  const signupLanguage =
    account?.account?.language_info?.browser_language ?? 'en';

  /** Dev only: VITE_DEV_SHOW_TRANSLATE_WIDGET=true|1 shows translate UI without a Spanish/PT plan. */
  const devTranslateFlag = (
    import.meta.env.VITE_DEV_SHOW_TRANSLATE_WIDGET ?? ''
  ).toLowerCase();
  const devForceTranslateWidget =
    import.meta.env.DEV &&
    (devTranslateFlag === 'true' || devTranslateFlag === '1');

  const translationLanguages = devForceTranslateWidget
    ? ['es']
    : (UKFraudScanVariation && signupLanguage !== 'en') ||
      spanishPlanEnabled ||
      portuguesePlanEnabled
    ? ['es', 'pt']
    : undefined;

  const browserLang = globalThis.navigator?.language?.slice(0, 2);
  const defaultLang = translationLanguages?.includes(browserLang as Lang)
    ? browserLang
    : 'en';

  // RFRSH-4335: Render experiment header (no canon Header changes needed)
  if (isInStandardSearchDesign) {
    return (
      <Box>
        <ExperimentHeader
          onSearchClick={() => setIsSearchPageOpen(true)}
          rightContent={
            <>
              <NotificationBell
                unreadCount={unreadNotificationCount}
                onClick={(e: React.MouseEvent<HTMLElement>) =>
                  setNotificationAnchorEl(e.currentTarget)
                }
              />
              <MenuToggle toggleAction={() => setIsMenuOpen(true)} />
            </>
          }
        />
        <HeaderMenu
          open={isMenuOpen}
          handleClose={() => setIsMenuOpen(false)}
          customStyle={{ width: 284 }}
        />
        <NotificationDropdown
          anchorEl={notificationAnchorEl}
          open={Boolean(notificationAnchorEl)}
          handleClose={() => setNotificationAnchorEl(null)}
          setUnreadNotificationCount={setUnreadNotificationCount}
        />
        <Dialog
          open={isSearchPageOpen}
          onClose={() => setIsSearchPageOpen(false)}
          fullScreen
          sx={{
            '& .MuiDialog-container': {
              alignItems: 'flex-start',
            },
          }}
          PaperProps={{
            sx: {
              bgcolor: 'white',
              height: 'auto',
              maxHeight: '100%',
              margin: 0,
            },
          }}
        >
          <StandardSearchPage
            onSearchSuccess={(data: any) => {
              handleSearchSuccess(data);
              setIsSearchPageOpen(false);
            }}
            onBack={() => setIsSearchPageOpen(false)}
            placement="header"
            searchFrom="search_header"
            visibleTabs={searchPageVisibleTabs}
            slotAfterTabs={({ hiddenTabIds, selectTab }) => {
              // Hidden carousel tab ids are ActiveTab values; those match
              // SearchPreferenceId 1:1 so the shared MoreSearchesDropdown can
              // jump/select without a translation map.
              const offscreenIds = hiddenTabIds as SearchPreferenceId[];
              const handleJumpToTab = (id: SearchPreferenceId) => {
                selectTab(id as ActiveTab);
              };
              return (
                <MoreSearchesDropdown
                  offscreenIds={offscreenIds}
                  onJumpToTab={handleJumpToTab}
                  onOpenPreferences={openPrefsModal}
                />
              );
            }}
          />
        </Dialog>
        <SearchPreferencesModal
          control={{
            isOpen: isPrefsModalOpen,
            open: openPrefsModal,
            close: () => setIsPrefsModalOpen(false),
          }}
          preferences={preferences}
        />
      </Box>
    );
  }

  return (
    <Box>
      <MobileDownloadSnackbar />
      <Header
        onSearchSuccess={handleSearchSuccess}
        translationOptions={{
          translationLanguages: translationLanguages as Lang[],
          defaultMountLanguage: defaultLang,
        }}
      />
    </Box>
  );
  // END RENDER
};
